import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";

import {
  cancelAPIRequest,
  classNames,
  currencyFormat,
  getErrorMessages,
} from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { useNavigate } from "react-router-dom";
import {
  clearUpdateBusinessService,
  clearUpdateServiceAddOn,
  clearUpdateServiceAddOnError,
  requestDeleteServiceAddOn,
  requestListAvailableAddOns,
  requestUpdateServiceAddOn,
  useAuthState,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { Pagination } from "../../Components/Pagination";
import TableSkeleton from "../../Components/TableSkeleton";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import { Modal } from "../../Components/Modal";
import { NumericFormat } from "react-number-format";
import { ModalConfirm } from "../../Components/ModalConfirm";
import FormLabel from "../../Components/FormLabel";
import { ModalAlert } from "../../Components/ModalAlert";

const minPrice = 1000;
const maxPrice = 50000000;

const BusinessServiceAddon = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const [page, setpage] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [addonId, setaddonId] = useState("");
  const [addonName, setaddonName] = useState("");
  const [description, setdescription] = useState("");
  const [price, setprice] = useState("");
  const [basePrice, setbasePrice] = useState("");

  const [nameError, setnameError] = useState(null);
  const [priceError, setpriceError] = useState(null);
  const [basePriceError, setbasePriceError] = useState(null);

  const businessServiceDispatch = useBusinessServiceDispatch();

  const {
    request,
    serviceAddons,
    serviceAddonsLoading,
    serviceAddonsError,
    nextTokens,
    addOnId,
    updateServiceAddOnError,
    updateServiceAddOnLoading,
    deleteServiceAddOnError,
    deleteServiceAddOnLoading,
    deletedAddOnID,
  } = useBusinessServiceState();

  useEffect(() => {
    return () => {
      clearUpdateServiceAddOn(businessServiceDispatch);
      clearUpdateServiceAddOnError(businessServiceDispatch);
    };
  }, [businessServiceDispatch]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessServiceDispatch, user.id]);

  useEffect(() => {
    if (addOnId) {
      setShowEditModal(false);
      setShowSuccessModal(true);
    } else if (updateServiceAddOnError) {
      setShowEditModal(false);
      setShowErrorModal(true);
    }
  }, [addOnId, updateServiceAddOnError]);

  useEffect(() => {
    const isserviceNameValid = addonName.length >= 10;
    const isPriceValid = price >= 0 && price >= minPrice && price <= maxPrice;
    const isBasePriceValid = basePrice > 0 ? basePrice > Number(price) : true;

    if (isserviceNameValid) {
      setErrorMessage("name", "");
    } else {
      if (addonName.length === 0) {
        setErrorMessage("name", "addOnNameRequired");
      } else {
        setErrorMessage("name", "minimumAddOnName");
      }
    }

    if (isPriceValid) {
      setErrorMessage("price", "");
    } else {
      if (price < minPrice) {
        setErrorMessage("price", "minimumPriceIs");
      } else if (price > maxPrice) {
        setErrorMessage("price", "maximumPriceIs");
      } else {
        setErrorMessage(
          "price",
          "Price is required and must be greater than 0."
        );
      }
    }

    if (isBasePriceValid) {
      setErrorMessage("basePrice", "");
    } else {
      setErrorMessage("basePrice", "basePriceRequired");
    }
  }, [addonName, price, basePrice]);

  useEffect(() => {
    if (!deletedAddOnID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedAddOnID]);

  useEffect(() => {
    if (deleteServiceAddOnError) {
      setShowConfirmModal(false);
      setShowErrorModal(true);
    }
  }, [deleteServiceAddOnError]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "name":
        setnameError(message);
        break;
      case "price":
        setpriceError(message);
        break;
      case "basePrice":
        setbasePriceError(message);
        break;
      default:
        break;
    }
  };

  const onLoad = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (user.id) {
      requestListAvailableAddOns(businessServiceDispatch, {
        limit: 10,
        page: 1,
        owner: user.id,
      });
    }
  };

  const validateInput = () => {
    let currentValidAddonName = true;
    let currentValidPrice = true;
    let currentValidBasePrice = true;

    const isserviceNameValid = addonName.length >= 10;
    const isPriceValid = price >= 0 && price >= minPrice && price <= maxPrice;
    const isBasePriceValid = basePrice > 0 ? basePrice > Number(price) : true;

    currentValidAddonName = isserviceNameValid;
    currentValidPrice = isPriceValid;
    currentValidBasePrice = isBasePriceValid;

    return currentValidAddonName && currentValidPrice && currentValidBasePrice;
  };

  const clearInputErrorMessages = () => {
    setErrorMessage("name", "");
    setErrorMessage("price", "");
    setErrorMessage("basePrice", "");
  };

  const onCreate = () => {
    clearUpdateServiceAddOnError(businessServiceDispatch);
    navigate(`/manage-business-service/addons/create`);
  };

  const onUpdate = () => {
    const validForm = validateInput();

    if (validForm) {
      const input = {
        id: addonId,
        name: addonName,
        description,
        price,
      };

      if (basePrice) {
        input.basePrice = basePrice;
      } else {
        input.basePrice = null;
      }

      requestUpdateServiceAddOn(businessServiceDispatch, { input });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListAvailableAddOns(businessServiceDispatch, {
        page: nextPage,
        limit: 10,
        owner: user.id,
        nextToken: nextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListAvailableAddOns(businessServiceDispatch, {
        page: prevPage,
        limit: 10,
        owner: user.id,
        nextToken: nextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListAvailableAddOns(businessServiceDispatch, {
        page: selectedPage,
        limit: 10,
        owner: user.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  const resetErrors = () => {
    setnameError("");
    setpriceError("");
    setbasePriceError("");
  };

  const onDelete = () => {
    requestDeleteServiceAddOn(businessServiceDispatch, {
      id: addonId,
    });
  };

  return (
    <div className="space-y-4">
      {(updateServiceAddOnError || deleteServiceAddOnError) && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(
            updateServiceAddOnError || deleteServiceAddOnError
          )}
        />
      )}
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description={
          deletedAddOnID
            ? "serviceAddOnSuccessDeleted"
            : "serviceAddOnSuccessChanged"
        }
        onConfirm={() => clearUpdateBusinessService(businessServiceDispatch)}
      />
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="deleteAddOn"
        description="deleteAddOnConfirmation"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deleteServiceAddOnLoading}
        disabledButton={deleteServiceAddOnLoading}
        onConfirm={onDelete}
        onCancel={() => {
          setShowConfirmModal(false);
          if (request) {
            request.cancel();
          }
        }}
      />
      <Modal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetErrors();
          clearInputErrorMessages();
        }}
        title={t("changeAddonDetail")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              type="submit"
              disabled={updateServiceAddOnLoading}
              loading={updateServiceAddOnLoading}
              title="Save"
              onClick={onUpdate}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-700">
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={addonName}
              placeholder={t("enterAddOnName")}
              onChange={(e) => setaddonName(e.target.value)}
              className={classNames(
                nameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {nameError && (
              <div className="mt-1 text-red-600 text-sm">{t(nameError)}</div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="description" label={t("Description")} />
            <textarea
              name="description"
              id="description"
              value={description}
              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500
                mt-1 block w-full text-sm rounded-md shadow-sm resize-none"
              placeholder={t("enterAddOnDescription")}
              onChange={(e) => setdescription(e.target.value)}
              rows={4}
              maxLength={480}
              minLength={10}
            />
          </div>
          <div>
            <FormLabel htmlFor="price" label={t("Price")} required />
            <NumericFormat
              type="text"
              name="price"
              id="price"
              className={classNames(
                priceError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
              placeholder={t("Enter Price")}
              thousandSeparator="."
              decimalSeparator=","
              displayType="input"
              prefix={"Rp"}
              value={price}
              onValueChange={(value) => {
                setprice(value.value);
                if (value.value < minPrice) {
                  setErrorMessage("price", "minimumPriceIs");
                } else if (value.value > maxPrice) {
                  setErrorMessage("price", "maximumPriceIs");
                } else {
                  setErrorMessage("price", "");
                }
              }}
            />
            {priceError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(priceError, {
                  minimumPrice: currencyFormat(minPrice),
                  maximumPrice: currencyFormat(maxPrice),
                })}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="basePrice" label={t("basePrice")} />
            <NumericFormat
              type="text"
              name="basePrice"
              id="basePrice"
              className={classNames(
                basePriceError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
              placeholder={t("enterBasePrice")}
              thousandSeparator="."
              decimalSeparator=","
              displayType="input"
              prefix={"Rp"}
              value={basePrice}
              onValueChange={(value) => {
                setbasePrice(value.value);
                if (value.value > 0 && value.value < Number(price)) {
                  setErrorMessage("basePrice", "basePriceRequired");
                } else {
                  setErrorMessage("basePrice", "");
                }
              }}
            />
            {basePriceError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(basePriceError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
      <div className="grid gird-cols-1 gap-y-4 mb-4">
        <div className="gap-4 flex justify-start w-full">
          <Button
            className="w-full sm:w-auto"
            type="button"
            onClick={onCreate}
            title={t("Create New")}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Description")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Price")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("basePrice")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                {serviceAddonsLoading ? (
                  <TableSkeleton rows={10} columns={6} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {serviceAddons.length > 0 ? (
                      serviceAddons.map((addon) => {
                        return (
                          <tr key={addon.id}>
                            <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                              {addon.name}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {t(addon.description)}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <NumericFormat
                                displayType={"text"}
                                value={addon.price}
                                thousandSeparator={"."}
                                decimalSeparator={","}
                                prefix={"Rp"}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              {Boolean(addon.basePrice) && (
                                <NumericFormat
                                  displayType={"text"}
                                  value={addon.basePrice}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                              <div className="flex flex-auto gap-2">
                                <button
                                  onClick={() => {
                                    setShowEditModal(true);
                                    setaddonId(addon.id);
                                    setaddonName(addon.name);
                                    setdescription(addon.description || "");
                                    setprice(addon.price);
                                    setbasePrice(addon.basePrice);
                                  }}
                                >
                                  <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                </button>
                                <button
                                  onClick={() => {
                                    setShowConfirmModal(true);
                                    setaddonId(addon.id);
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5 text-red-500" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="p-4" colSpan={6}>
                          {serviceAddonsError ? (
                            <ErrorList
                              message={
                                serviceAddonsError?.message ||
                                "Error fetching addOns."
                              }
                              errors={serviceAddonsError?.errors}
                            />
                          ) : (
                            <EmptyList title="No Data" />
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={nextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessServiceAddon;
